import { useCallback } from 'react';
import { useQuoteCurrency } from '../context';
import { formatArrivalWindow } from '../../../../lib/utils';

/**
 * The client side of `@codezo/quote-detail`'s formatting contract.
 *
 * Every module in that package takes money, dates and durations as ALREADY
 * FORMATTED strings, because the same modules render for an admin, a client and
 * a guest and each prices in its own currency off its own store. The admin app
 * keeps its half of this in `apps/admin/src/pages/quotes/v2/format.ts`; this is
 * the client's, reading the currency out of the view's context rather than a
 * store the package cannot see.
 *
 * `CurrencyText` stays the component for anywhere the client view draws money
 * itself. These are for the values handed ACROSS the boundary, where a component
 * is not accepted.
 */

export function useMoney() {
	const selectedCurrency = useQuoteCurrency();
	return useCallback(
		(
			amount: number | null | undefined,
			currency?: string | null,
			/** A per-piece price: up to 4 decimals when the figure needs them. */
			opts?: { unit?: boolean }
		): string | null => {
			if (amount == null) return null;
			// Totals always at 2 decimals — never "DKK 12.5".
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: currency || selectedCurrency,
				minimumFractionDigits: 2,
				maximumFractionDigits: opts?.unit ? 4 : 2,
			}).format(amount);
		},
		[selectedCurrency]
	);
}

/**
 * "18–24 days", collapsing to one figure when the bounds agree — and to "1 day"
 * when that figure is one.
 *
 * The singular is done by dropping a trailing "s", so it follows whatever unit
 * the caller passes and leaves abbreviations ("1 d") alone. It went unnoticed
 * while every caller abbreviated; the delivery table spelling the word out is
 * what turned it into "1 days" on screen.
 */
export function formatDayRange(
	min: number | null | undefined,
	max: number | null | undefined,
	unit = 'days'
): string | null {
	if (min == null && max == null) return null;
	const a = min ?? max!;
	const b = max ?? min!;
	if (a !== b) return `${a}–${b} ${unit}`;
	return `${a} ${a === 1 && unit.endsWith('s') ? unit.slice(0, -1) : unit}`;
}

/**
 * "arrives Sep 18 – Oct 2" from a projected window.
 *
 * Reads `ifAcceptedNow` — the lead time on its own. `ifAcceptedAtDeadline`
 * answers a different question ("what if I sit on this until the last day"),
 * and the badge beside this line is where that comparison belongs.
 */
export function formatArrival(
	projected: { ifAcceptedNow?: { earliest: string; latest: string } | null } | null | undefined
): string | null {
	const now = projected?.ifAcceptedNow;
	if (!now) return null;
	const window = formatArrivalWindow(now.earliest, now.latest);
	return window ? `arrives ${window}` : null;
}
